import type { BoggleRunSummaryScore } from '~/boggle/config/runSummaryScore'
import type {
  BoggleGameState,
  BoggleRunLedger,
  BoggleRunLedgerAcceptedWordEntry,
} from '~/boggle/engine/standard/types'
import {
  BOGGLE_GOLD_TILE_KIND,
  BOGGLE_SPECIAL_TILE_KINDS,
  getEffectiveGoldCount,
  type BoggleSpecialTileKind,
} from '~/boggle/engine/specialTiles'
import type { BoggleGameModeId, BoggleRunKind } from '~/boggle/gameModes'
import type { BoggleCompletedRunProgressSummary } from '~/boggle/progress/progressRepository'
import type { BoggleRunAttemptIdentity } from '~/boggle/progress/runCompletion'

const BOGGLE_TELEMETRY_SCHEMA_VERSION = 1

export type BoggleRunTelemetryReason = 'exit' | 'restart'

export type BoggleRunTelemetryBaseInput = {
  gameModeId: BoggleGameModeId
  runAttempt: BoggleRunAttemptIdentity
  runKind: BoggleRunKind
}

export type BoggleRunTelemetryState = Pick<
  BoggleGameState,
  'cols' | 'endReason' | 'phase' | 'selection' | 'timeMs' | 'tilesCleared'
> & {
  runLedger: BoggleRunLedger
}

export type BoggleRunTelemetryProperties = Record<
  string,
  boolean | null | number | string | Record<string, number> | undefined
>

function getAcceptedWordEntries(runLedger: BoggleRunLedger) {
  return runLedger.filter(
    (entry): entry is BoggleRunLedgerAcceptedWordEntry => entry.kind === 'accepted-word',
  )
}

function getSeedType(seedInput: unknown) {
  return typeof seedInput === 'string' ? 'string' : 'number'
}

function sumSpecialTileCounts(acceptedWords: readonly BoggleRunLedgerAcceptedWordEntry[]) {
  const counts = Object.fromEntries(
    BOGGLE_SPECIAL_TILE_KINDS.map(kind => [kind, 0]),
  ) as Record<BoggleSpecialTileKind, number>

  for (const entry of acceptedWords) {
    for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
      counts[kind] += entry.effectiveSpecialTileCountsUsed[kind] ?? 0
    }
  }

  return counts
}

function buildFrictionTelemetry(
  runLedger: BoggleRunLedger,
  abandonedSelectionLength = 0,
) {
  const acceptedWords = getAcceptedWordEntries(runLedger)
  const acceptedAtTimes = acceptedWords.map(entry => entry.atMs)
  const submitCount = runLedger.filter(
    entry => entry.kind === 'semantic-action' && entry.actionKind === 'submit',
  ).length
  const backspaceCount = runLedger.filter(
    entry => entry.kind === 'correction' && entry.correctionKind === 'backspace',
  ).length
  const clearCount = runLedger.filter(
    entry => entry.kind === 'correction' && entry.correctionKind === 'clear',
  ).length
  let maxGapBetweenAcceptedWordsMs: number | null = null
  for (let index = 1; index < acceptedAtTimes.length; index += 1) {
    const gap = acceptedAtTimes[index]! - acceptedAtTimes[index - 1]!
    maxGapBetweenAcceptedWordsMs = Math.max(maxGapBetweenAcceptedWordsMs ?? 0, gap)
  }

  return {
    abandonedSelectionLength,
    backspaceCount,
    clearCount,
    invalidSubmitCount: Math.max(0, submitCount - acceptedWords.length),
    maxGapBetweenAcceptedWordsMs,
    submitCount,
    timeToFirstAcceptedWordMs: acceptedAtTimes[0] ?? null,
  }
}

function buildRunBaseTelemetry({
  gameModeId,
  runAttempt,
  runKind,
}: BoggleRunTelemetryBaseInput): BoggleRunTelemetryProperties {
  const setup = runAttempt.setupSnapshot
  const challenge = setup?.challenge

  const properties: BoggleRunTelemetryProperties = {
    boardCols: setup?.boardCols,
    boardRows: setup?.boardRows,
    eventSchemaVersion: BOGGLE_TELEMETRY_SCHEMA_VERSION,
    gameModeId,
    runId: runAttempt.id,
    runKind,
    seedType: getSeedType(setup?.seedInput),
  }

  if (challenge) {
    properties.aceScore = challenge.aceScore
    properties.challengeConfigurationId = challenge.configurationId ?? undefined
    properties.challengeProgressSeed =
      challenge.progressSeed === undefined ? undefined : String(challenge.progressSeed)
    properties.targetScore = challenge.targetScore
  }

  return properties
}

function hasRunPhase(runLedger: BoggleRunLedger, phases: readonly string[]) {
  return runLedger.some(entry => entry.kind === 'phase' && phases.includes(entry.phase))
}

function buildRunSummaryTelemetry(
  state: BoggleRunTelemetryState,
  summaryScore?: BoggleRunSummaryScore,
): BoggleRunTelemetryProperties {
  const acceptedWords = getAcceptedWordEntries(state.runLedger)
  const wordLengths = acceptedWords.map(entry => entry.word.length)
  const wordLengthTotal = wordLengths.reduce((sum, length) => sum + length, 0)
  const specialTileCounts = sumSpecialTileCounts(acceptedWords)
  const runEnd = [...state.runLedger].reverse().find(entry => entry.kind === 'run-end')
  const durationMs = runEnd?.atMs ?? state.timeMs
  const uniqueWordCount = new Set(acceptedWords.map(entry => entry.word)).size

  return {
    acceptedWordCount: acceptedWords.length,
    averageWordLength:
      acceptedWords.length === 0 ? null : wordLengthTotal / acceptedWords.length,
    durationMs,
    endReason: runEnd?.kind === 'run-end' ? runEnd.endReason : state.endReason,
    goldTilesPlayed: getEffectiveGoldCount(specialTileCounts),
    longestWordLength: wordLengths.length === 0 ? null : Math.max(...wordLengths),
    meterBurstEntered:
      hasRunPhase(state.runLedger, ['gold-rush', 'meter-burst']) ||
      acceptedWords.some(
        entry =>
          entry.duringMeterBurstWindow ||
          entry.burstActiveAtAcceptance ||
          entry.meterBurstActiveAfter,
      ),
    meterEarned: acceptedWords.reduce((sum, entry) => sum + entry.meterGain, 0),
    overmeterEarned: acceptedWords.reduce((sum, entry) => sum + entry.overmeterGain, 0),
    overtimeEntered:
      hasRunPhase(state.runLedger, ['overtime']) ||
      acceptedWords.some(entry => entry.acceptedDuringOvertime),
    physicalGoldTilesPlayed: acceptedWords.reduce(
      (sum, entry) => sum + (entry.physicalSpecialTileCountsUsed[BOGGLE_GOLD_TILE_KIND] ?? 0),
      0,
    ),
    ratingId: summaryScore?.rating.id,
    score: summaryScore?.total,
    specialTileCounts,
    tilesCleared: state.tilesCleared,
    uniqueWordCount,
  }
}

function buildRunProgressTelemetry(
  progressSummary?: BoggleCompletedRunProgressSummary,
): BoggleRunTelemetryProperties {
  if (!progressSummary) {
    return {}
  }

  return {
    completedRunCount: progressSummary.completedRunCount,
    completedRunIndex: progressSummary.completedRunIndex,
    improvedBadgeCount: progressSummary.improvedBadgeCount,
    newAchievementTierCount: progressSummary.newAchievementTierCount,
    newBadgeCount: progressSummary.newBadgeCount,
    newCommonBadgeCount: progressSummary.newBadgeCountsByLevel.common,
    newBadgeCountsByLevel: progressSummary.newBadgeCountsByLevel,
    newImpossibleBadgeCount: progressSummary.newBadgeCountsByLevel.impossible,
    newLegendaryBadgeCount: progressSummary.newBadgeCountsByLevel.legendary,
    newRatingRevealCount: progressSummary.newRatingRevealCount,
    newRareBadgeCount: progressSummary.newBadgeCountsByLevel.rare,
    newUncommonBadgeCount: progressSummary.newBadgeCountsByLevel.uncommon,
    newUniqueWordCount: progressSummary.newUniqueWordCount,
    totalAchievementTierCount: progressSummary.totalAchievementTierCount,
    totalBadgeCount: progressSummary.totalBadgeCount,
    totalBadgeCountsByLevel: progressSummary.totalBadgeCountsByLevel,
    totalCommonBadgeCount: progressSummary.totalBadgeCountsByLevel.common,
    totalImpossibleBadgeCount: progressSummary.totalBadgeCountsByLevel.impossible,
    totalLegendaryBadgeCount: progressSummary.totalBadgeCountsByLevel.legendary,
    totalRareBadgeCount: progressSummary.totalBadgeCountsByLevel.rare,
    totalRevealedRatingCount: progressSummary.totalRevealedRatingCount,
    totalUncommonBadgeCount: progressSummary.totalBadgeCountsByLevel.uncommon,
    totalUniqueWordCount: progressSummary.totalUniqueWordCount,
  }
}

export function buildBoggleRunStartedTelemetryProperties(
  input: BoggleRunTelemetryBaseInput,
) {
  return buildRunBaseTelemetry(input)
}

export function buildBoggleRunCompletedTelemetryProperties(
  input: BoggleRunTelemetryBaseInput & {
    progressSummary?: BoggleCompletedRunProgressSummary
    state: BoggleRunTelemetryState
    summaryScore: BoggleRunSummaryScore
  },
): BoggleRunTelemetryProperties {
  return {
    ...buildRunBaseTelemetry(input),
    ...buildRunSummaryTelemetry(input.state, input.summaryScore),
    ...buildFrictionTelemetry(input.state.runLedger),
    ...buildRunProgressTelemetry(input.progressSummary),
  }
}

export function buildBoggleRunAbandonedTelemetryProperties(
  input: BoggleRunTelemetryBaseInput & {
    reason: BoggleRunTelemetryReason
    state: BoggleRunTelemetryState
  },
) {
  return {
    ...buildRunBaseTelemetry(input),
    ...buildRunSummaryTelemetry(input.state),
    ...buildFrictionTelemetry(input.state.runLedger, input.state.selection.length),
    abandonReason: input.reason,
  }
}
